import { Statistic, Typography, theme } from 'antd';
import type { ReactNode } from 'react';
import { formatCurrency } from '../../../lib/format';
import { DashboardCard } from '../../../components/DashboardCard';
import { CardEyebrow } from '../../../components/CardEyebrow';

const { Text } = Typography;

export type KpiAccent = 'cobros' | 'pagos' | 'clientes' | 'mora' | 'none';

interface KpiCardProps {
  label: string;
  value: string;
  /** Single line or a custom node (e.g. two stacked lines of context). */
  caption?: ReactNode;
  formatValue?: boolean;
  accent?: KpiAccent;
  /** No data for the period: renders "—" instead of a value. */
  empty?: boolean;
  /** Composition segments rendered as a thin stacked bar under the value. */
  stack?: { value: number; color: string }[];
  /** Lower-emphasis treatment for supporting operational metrics. */
  compact?: boolean;
}

const ACCENT_TOKEN: Record<
  Exclude<KpiAccent, 'none'>,
  'colorPrimary' | 'colorSuccess' | 'colorInfo' | 'colorError'
> = {
  cobros: 'colorPrimary',
  pagos: 'colorSuccess',
  clientes: 'colorInfo',
  mora: 'colorError',
};

export function KpiCard({
  label,
  value,
  caption,
  formatValue = true,
  accent = 'none',
  empty = false,
  stack,
  compact = false,
}: KpiCardProps) {
  const displayValue = empty ? '—' : formatValue ? formatCurrency(value) : value;
  const { token } = theme.useToken();
  const accentColor = accent === 'none' ? null : token[ACCENT_TOKEN[accent]];
  const stackTotal = stack?.reduce((sum, segment) => sum + segment.value, 0) ?? 0;

  return (
    <DashboardCard
      size="small"
      style={{ height: '100%' }}
      bodyStyle={{ padding: compact ? '14px 18px 16px' : '18px 22px 20px' }}
    >
      <CardEyebrow
        label={label}
        color={accentColor ?? undefined}
        style={{ marginBottom: compact ? 8 : 12 }}
      />
      <Statistic
        value={displayValue}
        styles={{
          content: {
            fontSize: compact ? 21 : 26,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            lineHeight: 1.2,
            color: empty ? token.colorTextQuaternary : undefined,
          },
        }}
      />
      {stack && stackTotal > 0 && (
        <div
          data-testid="kpi-stack"
          style={{
            display: 'flex',
            height: 6,
            borderRadius: 3,
            overflow: 'hidden',
            marginTop: 12,
          }}
        >
          {stack.map((segment, index) => (
            <div
              key={index}
              style={{
                width: `${(segment.value / stackTotal) * 100}%`,
                backgroundColor: segment.color,
              }}
            />
          ))}
        </div>
      )}
      {caption && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: compact ? 8 : 10,
            minHeight: 16,
          }}
        >
          {typeof caption === 'string' ? (
            <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.3 }}>
              {caption}
            </Text>
          ) : (
            caption
          )}
        </div>
      )}
    </DashboardCard>
  );
}
